<script setup lang="ts">
import type { SolrNewestDocsParams } from '~/composables/useNewestDocsQuery'
import { faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons'
import { SolrParamsKey } from '~~/shared/solr'
import { truncateHtmlContent } from '~/utils/html'

const readMore = ref<boolean>(false)

const MAX_SLIDES_TO_SCROLL = 3
const CAROUSEL_PAGES = 3
const MAX_NEWS = MAX_SLIDES_TO_SCROLL * CAROUSEL_PAGES

const queryParams = computed<SolrNewestDocsParams>(() => ({
  [SolrParamsKey.Newest]: {
    rows: MAX_NEWS,
    start: 0,
    contentType: 'news_notice',
  },
}))

const { data, isLoading, isError } = useNewestDocsQuery(queryParams)

const allNews = computed(() => data.value?.response?.docs ?? [])

const { width } = useWindowSize()

const slidesToScroll = computed(() => {
  if (width.value < 768) {
    return 1
  }
  if (width.value < 1440) {
    return 2
  }
  return MAX_SLIDES_TO_SCROLL
})

const isMounted = useMounted()

const lastestNews = computed(() => isMounted.value ? allNews.value.slice(0, slidesToScroll.value * CAROUSEL_PAGES) : allNews.value)

// This will be removed when we have proper bodytext in Solr for news items
const { $parseStringToDocument } = useNuxtApp()

function extractFirstParagraphHtml(html: string | undefined): string {
  if (!html) {
    return ''
  }

  const MAX_CHARS = 400

  try {
    const doc = $parseStringToDocument(html, ['process-news-links'])
    const firstP = doc.body.querySelector('p')

    if (!firstP) {
      const textContent = doc.body.textContent?.trim() || ''
      if (textContent.length <= MAX_CHARS) {
        return doc.body.innerHTML
      }
      return textContent.slice(0, MAX_CHARS)
    }

    const textContent = firstP.textContent?.trim() || ''
    if (textContent.length <= MAX_CHARS) {
      return firstP.innerHTML
    }

    return truncateHtmlContent(firstP, MAX_CHARS)
  }
  catch (error) {
    console.error('Failed to extract paragraph HTML:', error)
    return ''
  }
}
</script>

<template>
  <div class="flex flex-col gap-m w-full max-w-[1348px] py-m px-s 3xl:pl-0 2xl:pr-m">
    <h2 class="title-m !font-normal">
      Siste nytt
    </h2>

    <Carousel v-if="isLoading" aria-label="Siste nytt laster" :slides-to-scroll="slidesToScroll" variant="default" controls>
      <div v-for="n in MAX_NEWS" :key="`skeleton-${n}`">
        <OverviewLatestNewsItemSkeleton />
      </div>
    </Carousel>

    <div v-else-if="isError" class="flex items-center justify-center py-xl w-full">
      <div role="alert" class="text-error">
        Kunne ikke laste nyheter
      </div>
    </div>

    <Carousel v-else-if="lastestNews.length > 0" aria-label="Siste nytt" :slides-to-scroll="slidesToScroll" controls dots variant="default">
      <div v-for="doc in lastestNews" :key="doc.id" class="flex transition-height duration-500" :class="readMore ? 'md:h-[300px]' : 'md:h-[120px]'">
        <div class="flex items-stretch gap-s w-full">
          <div class="flex flex-col gap-2xs justify-start w-[calc(100vw-8.5rem)] md:w-[399px]">
            <div class="flex flex-col gap-xs w-full">
              <div class="flex items-center gap-2xs text-caption font-normal">
                <span class="border-r border-r-blurple-200 pr-2xs">{{ doc.noticeType }}</span>
                <span v-if="doc.date">{{ formatDate(new Date(doc.date), { day: '2-digit', month: 'long', year: 'numeric' }) }}</span>
              </div>
              <p class="text-base md:font-bold">
                {{ doc.title }}
              </p>
            </div>
            <p class="text-base" :class="readMore ? 'line-clamp-none' : 'line-clamp-2'" v-html="extractFirstParagraphHtml(doc.bodytext)" />
          </div>
          <Divider orientation="vertical" class="!border-blurple-200" aria-hidden="true" />
        </div>
      </div>
    </Carousel>

    <div v-else class="flex items-center justify-center py-xl w-full">
      <span class="text-secondary">Ingen nyheter funnet</span>
    </div>

    <Button
      v-if="!isLoading && lastestNews.length > 0"
      :icon="readMore ? faChevronUp : faChevronDown"
      icon-position="right"
      variant="tertiary"
      class="!p-0 !text-primary text-small !gap-2xs !bg-transparent mx-auto"
      size="medium"
      @click="readMore = !readMore"
    >
      {{ readMore ? 'Se mindre' : 'Se mer' }}
    </Button>
  </div>
</template>
